import React, { useEffect, useId, useMemo, useRef } from 'react';
import { Box, Button, Flex, FlexItem, Text } from '@bigcommerce/big-design';
import {
  QUESTION_STATUSES,
  QUESTION_STATUS_LABELS,
  type ApiErrorResponse,
  type QuestionSolutionConflict,
  type QuestionStatus,
} from '@find-my-part/types';

export type SolutionWriteAction = 'mark' | 'unmark';
export type SolutionWriteIssueKind =
  | 'conflict'
  | 'refresh_required'
  | 'ambiguous'
  | 'ready'
  | 'repair_required'
  | 'converged';

interface SolutionWriteIssueBase {
  action: SolutionWriteAction;
  replyId: string;
  expectedStatus?: QuestionStatus;
  targetStatus: QuestionStatus;
  /** Stable across recovery updates so one event is announced exactly once. */
  eventId?: number;
  /** Static alert copy for the original event; mutable recovery stays outside it. */
  announcementKind?: SolutionWriteIssueKind;
}

export type SolutionWriteIssue =
  | (SolutionWriteIssueBase & {
      kind: 'conflict';
      observedStatus: QuestionStatus;
    })
  | (SolutionWriteIssueBase & { kind: 'refresh_required' })
  | (SolutionWriteIssueBase & { kind: 'ambiguous' })
  | (SolutionWriteIssueBase & {
      kind: 'ready';
      observedStatus: QuestionStatus;
    })
  | (SolutionWriteIssueBase & {
      kind: 'repair_required';
      observedStatus: QuestionStatus;
    })
  | (SolutionWriteIssueBase & {
      kind: 'converged';
      observedStatus: QuestionStatus;
    });

const CONFLICT_KEYS = new Set(['surface', 'contested', 'expected', 'observed', 'recovery']);

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isQuestionStatus(value: unknown): value is QuestionStatus {
  return typeof value === 'string' && (QUESTION_STATUSES as readonly string[]).includes(value);
}

function readStatusState(value: unknown): { status: QuestionStatus } | null {
  if (!isRecord(value) || Object.keys(value).length !== 1 || !isQuestionStatus(value.status)) {
    return null;
  }
  return { status: value.status };
}

/** Strictly decode ADR-027's bounded solution conflict contract. */
export function readQuestionSolutionConflict(
  error: ApiErrorResponse['error']
): QuestionSolutionConflict | null {
  if (error.code !== 'STALE_WRITE') return null;
  const value = error.details?.conflict;
  if (!isRecord(value) || !Object.keys(value).every((key) => CONFLICT_KEYS.has(key))) return null;
  const expected = readStatusState(value.expected);
  const observed = readStatusState(value.observed);
  if (
    value.surface !== 'question_solution' ||
    value.recovery !== 'direct' ||
    !expected ||
    !observed ||
    !Array.isArray(value.contested) ||
    value.contested.length !== 1 ||
    value.contested[0] !== 'status'
  ) {
    return null;
  }
  return {
    surface: 'question_solution',
    contested: ['status'],
    expected,
    observed,
    recovery: 'direct',
  };
}

/** A solution mutation may have committed before these outcomes became visible. */
export function isAmbiguousSolutionFailure(error: ApiErrorResponse['error']): boolean {
  return [
    'INTERNAL_ERROR',
    'NETWORK_ERROR',
    'REQUEST_TIMEOUT',
    'MAX_RETRIES_EXCEEDED',
    'SERVICE_UNAVAILABLE',
  ].includes(error.code);
}

interface SolutionWriteNoticeProps {
  issue: SolutionWriteIssue | null;
  busy: boolean;
  onRecover?: () => void;
  onDismiss?: () => void;
  restoreFocusTo?: () => HTMLElement | null;
  testId?: string;
}

const SR_ONLY_STYLE: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
};

function intendedChangeLabel(action: SolutionWriteAction): string {
  return action === 'mark' ? 'Mark this reply as the solution' : 'Remove this reply as the solution';
}

/**
 * Persistent, display-safe recovery notice for solution mark/unmark writes.
 * The assertive region contains a static event summary; mutable comparison and
 * recovery controls live in a separately named, focusable region.
 */
export function SolutionWriteNotice({
  issue,
  busy,
  onRecover,
  onDismiss,
  restoreFocusTo,
  testId = 'solution-write',
}: SolutionWriteNoticeProps): React.ReactElement {
  const regionRef = useRef<HTMLDivElement>(null);
  const previouslyVisibleRef = useRef(false);
  const focusRestorePendingRef = useRef(false);
  const restoreFocusToRef = useRef(restoreFocusTo);
  const headingId = useId();

  useEffect(() => {
    restoreFocusToRef.current = restoreFocusTo;
  }, [restoreFocusTo]);

  useEffect(() => {
    const wasVisible = previouslyVisibleRef.current;
    previouslyVisibleRef.current = issue !== null;
    if (issue) {
      focusRestorePendingRef.current = false;
      if (!busy) regionRef.current?.focus();
      return undefined;
    }
    if (wasVisible) focusRestorePendingRef.current = true;
    if (busy || !focusRestorePendingRef.current || !restoreFocusToRef.current) return undefined;
    const timer = window.setTimeout(() => {
      const target = restoreFocusToRef.current?.();
      if (target && document.contains(target) && !target.matches(':disabled')) {
        target.focus();
        focusRestorePendingRef.current = false;
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [busy, issue]);

  const announcementKind = issue?.announcementKind ?? issue?.kind;
  const announcement = issue
    ? announcementKind === 'ambiguous'
      ? 'Your solution change may have been saved. Refresh to confirm.'
      : announcementKind === 'refresh_required'
        ? 'This question changed while you were editing. Refresh before reapplying your solution change.'
        : 'Another staff member changed this question. Review the solution conflict notice.'
    : '';

  const heading = useMemo(() => {
    if (!issue) return '';
    if (issue.kind === 'ambiguous') return 'Your solution change may have been saved.';
    if (issue.kind === 'converged') return 'The solution state is already current.';
    if (issue.kind === 'repair_required') return 'The solution state needs review.';
    if (issue.kind === 'ready') return 'The current solution state has been refreshed.';
    if (issue.kind === 'refresh_required') return 'This question changed while you were editing.';
    return 'Another staff member changed this question.';
  }, [issue]);

  const observedStatus =
    issue && 'observedStatus' in issue ? issue.observedStatus : undefined;
  const statusMatchesTarget = observedStatus === issue?.targetStatus;
  const recoveryLabel =
    issue?.kind === 'ambiguous'
      ? 'Refresh to confirm'
      : issue?.kind === 'ready'
        ? 'Reapply my solution change'
        : issue?.action === 'unmark'
          ? 'Refresh and review my solution change'
        : statusMatchesTarget
          ? 'Refresh to confirm or repair'
          : 'Refresh and reapply my solution change';
  const recoveryLoadingLabel =
    issue?.kind === 'ambiguous'
      ? 'Refreshing solution state to confirm the change'
      : issue?.action === 'unmark' && issue.kind !== 'ready'
        ? 'Refreshing solution state for review'
      : 'Refreshing solution state and reapplying the change';

  return (
    <>
      <div role="alert" data-testid={`${testId}-alert`} style={SR_ONLY_STYLE}>
        {issue ? (
          // `useReplies.publishSolutionWriteIssue` assigns a stable event id
          // and preserves it through recovery-state changes. Key directly on
          // that identity so a new event remounts the static alert exactly
          // once without mutating refs during render.
          <span key={issue.eventId}>{announcement}</span>
        ) : null}
      </div>
      {issue ? (
        <div
          ref={regionRef}
          role="region"
          aria-labelledby={headingId}
          tabIndex={-1}
          data-testid={`${testId}-notice`}
          style={{ outline: 'none' }}
        >
          <Box
            backgroundColor="warning10"
            borderRadius="normal"
            padding="small"
            marginBottom="medium"
          >
            <Text id={headingId} bold marginBottom="xxSmall">
              {heading}
            </Text>

            {issue.expectedStatus ? (
              <Text marginBottom="xxSmall" data-testid={`${testId}-expected`}>
                Question status when you acted: {QUESTION_STATUS_LABELS[issue.expectedStatus]}
              </Text>
            ) : null}
            {observedStatus ? (
              <Text marginBottom="xxSmall" data-testid={`${testId}-current`}>
                Current question status: {QUESTION_STATUS_LABELS[observedStatus]}
              </Text>
            ) : null}
            <Text marginBottom="small" data-testid={`${testId}-intended`}>
              Your intended change: {intendedChangeLabel(issue.action)}
            </Text>

            <Text marginBottom="small">
              {issue.kind === 'ambiguous'
                ? 'Your change may have been saved — refresh to confirm before trying again. Your solution choice is preserved.'
                : issue.kind === 'refresh_required'
                  ? 'The conflict details could not be displayed safely. Your solution choice is preserved; refresh the current state before reapplying it.'
                  : issue.kind === 'converged'
                    ? issue.action === 'mark'
                      ? 'This reply is already marked as the solution — no action needed.'
                      : 'This reply is already no longer marked as the solution — no action needed.'
                    : issue.kind === 'repair_required'
                      ? issue.action === 'unmark'
                        ? 'The refreshed solution state cannot be safely repaired by repeating the removal. Your context is preserved; review the question status before choosing another action.'
                        : 'The refreshed question status and reply flag do not form a state this control can safely repair. Your context is preserved; review the question status before choosing another action.'
                    : statusMatchesTarget
                      ? issue.action === 'mark'
                        ? 'The question is already resolved, but status alone does not confirm this reply’s solution flag. Refresh to confirm it; an interrupted mark can be safely reapplied to repair the flag.'
                        : 'The question is already open, but status alone does not confirm that this reply’s solution flag was removed. Refresh to review the current state; this control will not repeat the removal automatically.'
                      : issue.kind === 'ready'
                        ? 'The refreshed reply state does not match your intended change. Reapply your preserved solution choice.'
                        : 'Review the current question status, then refresh the baseline and reapply your unchanged solution choice.'}
            </Text>

            <Flex flexGap="8px" flexWrap="wrap">
              {issue.kind !== 'converged' &&
              issue.kind !== 'repair_required' &&
              onRecover ? (
                <FlexItem>
                  <Button
                    variant="primary"
                    onClick={onRecover}
                    disabled={busy}
                    isLoading={busy}
                    aria-label={busy ? recoveryLoadingLabel : recoveryLabel}
                  >
                    {recoveryLabel}
                  </Button>
                </FlexItem>
              ) : null}
              {onDismiss ? (
                <FlexItem>
                  <Button variant="secondary" onClick={onDismiss} disabled={busy}>
                    {issue.kind === 'converged' ? 'Continue' : 'Dismiss'}
                  </Button>
                </FlexItem>
              ) : null}
            </Flex>
          </Box>
        </div>
      ) : null}
    </>
  );
}
